import type { Cam, CamStatus, Layout, Gutter, Quality, WallCell } from '@/engine/types';
import { STREAM_BASE, SITE_TH, SEEDS, SITE_CODES, SLOT_COUNT } from '@/config';
import { loadRaw, saveRaw } from './persist';
import { migrateLegacy } from './migrate';
import * as bus from '@/lib/bus';

export interface AppState {
  cameras: Record<string, Cam>;
  onWall: WallCell[]; // sparse for fixed layouts (index = slot), compact for 'fit'
  layout: Layout;
  gutter: Gutter;
  labels: Record<string, string>;
  lastScan: number | null;
  scanCache: Record<string, { status: string; at: number }>;
  panelOpen: boolean;
  sites: string[];
  globalQuality: Quality;
  quality: Record<string, Quality>; // per-camera override
  view: ViewFilter;
}

/** Which source the wall (and panel list) shows. */
export type ViewSource = 'all' | 'canteen' | 'cctv';
export interface ViewFilter {
  source: ViewSource;
  onlineOnly: boolean; // hide cameras seen offline/retired
  unlimited: boolean;  // lift the concurrent-decoder cap
}

export function parseId(id: string): { site: string | null; idx: string | null } {
  const m = /^CAM-([A-Za-z0-9]+)-(\d+)$/.exec(id);
  return m ? { site: m[1]!.toUpperCase(), idx: m[2]! } : { site: null, idx: null };
}

export function mkCam(id: string, url?: string | null, status: Cam['status'] = 'unknown'): Cam {
  const p = parseId(id);
  return { id, site: p.site, idx: p.idx, url: url || `${STREAM_BASE}/${id}/`, status, lastSeen: null };
}

function createDefault(): AppState {
  const cameras: Record<string, Cam> = {};
  for (const id of SEEDS) cameras[id] = mkCam(id, null, 'online');
  return {
    cameras,
    onWall: [...SEEDS],
    layout: 'fit',
    gutter: 'dense',
    labels: {},
    lastScan: null,
    scanCache: {},
    panelOpen: true,
    sites: SITE_CODES.filter((c) => c.k).map((c) => c.c),
    globalQuality: '360p',
    quality: {},
    view: { source: 'all', onlineOnly: false, unlimited: false },
  };
}

export const state: AppState = (() => {
  const loaded = loadRaw() ?? migrateLegacy();
  if (!loaded) return createDefault();
  const def = createDefault();
  // shallow-merge so new fields always exist
  const s: AppState = { ...def, ...loaded };
  if (!s.sites || !s.sites.length) s.sites = def.sites;
  if (!s.globalQuality) s.globalQuality = def.globalQuality;
  if (!s.quality) s.quality = {};
  s.view = { ...def.view, ...(s.view ?? {}) };
  // 'offline' is a stale observation from an earlier session: let it be retried
  // (and shown under "online only") until this session sees it fail. 'absent'
  // means retired upstream and stays.
  for (const c of Object.values(s.cameras)) if (c.status === 'offline') c.status = 'unknown';
  return s;
})();

// ---- persistence + pub/sub ----
const listeners = new Set<() => void>();
export const subscribe = (fn: () => void): void => void listeners.add(fn);
export const notify = (): void => listeners.forEach((fn) => fn());

let saveTimer: number | undefined;
export function save(): void {
  clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => saveRaw(state), 120);
}

// ---- derived helpers ----
export function labelOf(cam: Cam): string {
  if (state.labels[cam.id]) return state.labels[cam.id]!;
  if (cam.site) return `${SITE_TH[cam.site] ?? cam.site} ${cam.idx ?? ''}`.trim();
  return cam.id;
}

export const effectiveQuality = (id: string): Quality => state.quality[id] ?? state.globalQuality;

/** Update a camera's status; persists and notifies the panel to repaint its dot. */
export function markCam(cam: Cam, status: CamStatus): void {
  if (cam.status === status) return;
  cam.status = status;
  if (status === 'live' || status === 'online') cam.lastSeen = Date.now();
  save();
  bus.emit('cam:status', cam.id);
}

/** Cameras actually on the wall (compact, no holes). */
export const wallCams = (): string[] => state.onWall.filter((x): x is string => !!x);

/** Whether a camera passes the source filter (wall + panel list). */
export function matchesSource(cam: Cam, src: ViewSource = state.view.source): boolean {
  if (src === 'all') return true;
  return (cam.provider === 'yoonee') === (src === 'cctv');
}

/** Whether a camera passes the whole view filter. */
export function inView(cam: Cam): boolean {
  if (!matchesSource(cam)) return false;
  if (state.view.onlineOnly && (cam.status === 'offline' || cam.status === 'absent')) return false;
  return true;
}

/** Any filter narrowing the wall? (A filtered wall is always shown as 'fit'.) */
export const viewFiltered = (): boolean => state.view.source !== 'all' || state.view.onlineOnly;

/** Wall cameras that pass the view filter, in wall order — what is rendered. */
export const viewCams = (): string[] => wallCams().filter((id) => {
  const c = state.cameras[id];
  return !!c && inView(c);
});

/**
 * Reshape onWall for the given layout: 'fit' is compact; fixed layouts pad to
 * SLOT_COUNT with nulls and re-pack any overflow into the earliest holes.
 * Returns the ids that were dropped (overflow that did not fit).
 */
export function normalizeForLayout(layout: Layout = state.layout): string[] {
  if (layout === 'fit') {
    state.onWall = wallCams();
    return [];
  }
  const n = SLOT_COUNT[layout];
  const overflow = state.onWall.slice(n).filter((x): x is string => !!x);
  state.onWall = state.onWall.slice(0, n);
  while (state.onWall.length < n) state.onWall.push(null);
  const dropped: string[] = [];
  for (const cam of overflow) {
    const hole = state.onWall.indexOf(null);
    if (hole === -1) { dropped.push(cam); continue; }
    state.onWall[hole] = cam;
  }
  return dropped;
}
